import React, { useEffect, useState } from "react";
import { Table, Button, Modal, Form } from "react-bootstrap";
import {
  getTutors,
  addTutor as createTutor,
  deleteUser as deleteTutor,
  updateUser as updateTutor,
  updateUserPassword,
  updateUserStatus,
} from "../services/userService";
import Loader from "../components/Loader";
import ToggleBtn from "../components/ToggleBtn";
import { toast } from "react-toastify";
import { PenIcon, TrashIcon } from "lucide-react";
import { useAuth } from "../context/AuthProvider";
import { USER_ROLES } from "../utils";

const Tutors = () => {
  const [tutors, setTutors] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    qualification: "",
    experience: "",
    role: USER_ROLES.TUTOR,
  });
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const pageSize = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState("");
  const [sortKey, setSortKey] = useState("name");
  const [sortAsc, setSortAsc] = useState(true);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const { user } = useAuth();

  const fetchTutors = async () => {
    setLoading(true);
    const data = await getTutors();
    setTutors(data);
    setLoading(false);
  };

  useEffect(() => {
    fetchTutors();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editingId) {
      setBtnLoading(true);
      try {
        const originalTutor = tutors.find(t => t.id === editingId);
        const roleChanged = originalTutor && originalTutor.role !== formData.role;
        
        // Confirm role change
        if (roleChanged) {
          const confirmChange = window.confirm(
            `Are you sure you want to change this user's role from "${originalTutor.role}" to "${formData.role}"?\n\n` +
            `This will move the user to a different category and may affect their access permissions.`
          );
          if (!confirmChange) {
            setBtnLoading(false);
            return;
          }
        }
        
        await updateTutor(editingId, formData);
        
        if (roleChanged) {
          toast.success(`Tutor updated successfully! Role changed from ${originalTutor.role} to ${formData.role}.`);
          toast.info("The user will now appear in the appropriate section based on their new role.");
        } else {
          toast.success("Tutor updated successfully");
        }
      } catch (error) {
        toast.error("Failed to update tutor");
        console.error(error);
      }
    } else {
      setBtnLoading(true);
      try {
        await createTutor(formData);
        toast.success("Tutor added successfully");
      } catch (error) {
        toast.error("Failed to add tutor");
        console.error(error);
      }
    }
    setBtnLoading(false);
    setShowModal(false);
    setFormData({
      name: "",
      email: "",
      phone: "",
      qualification: "",
      experience: "",
      role: USER_ROLES.TUTOR,
    });
    setEditingId(null);
    fetchTutors();
  };

  const handleEdit = (tutor) => {
    setFormData(tutor);
    setEditingId(tutor.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const tutor = tutors.find((t) => t.id === id);
    const tutorName = tutor?.name || "this tutor";
    
    const confirmMessage = `Are you sure you want to delete ${tutorName}?\n\n` +
      `This will permanently delete:\n` +
      `• User account and profile\n` +
      `• All attendance records\n` +
      `• All leave applications\n` +
      `• All task assignments\n` +
      `• All salary configurations and records\n` +
      `• All class records and student assignments\n` +
      `• All activity history\n\n` +
      `This action cannot be undone!`;
      
    if (!window.confirm(confirmMessage)) return;
    
    setLoading(true);
    try {
      const result = await deleteTutor(id);
      setLoading(false);
      const updatedTutors = tutors.filter((t) => t.id !== id);
      setTutors(updatedTutors);
      toast.success(`${tutorName} and all associated records deleted successfully`);
      console.log("Deletion result:", result);
    } catch (error) {
      setLoading(false);
      toast.error(`Failed to delete ${tutorName}: ${error.message}`);
      console.error("Delete error:", error);
    }
  };

  const filteredTutors = tutors
    .filter((s) =>
      Object.values(s).some((value) =>
        String(value).toLowerCase().includes(filter.toLowerCase())
      )
    )
    .sort((a, b) => {
      const aValue = String(a[sortKey]).toLowerCase();
      const bValue = String(b[sortKey]).toLowerCase();
      if (aValue < bValue) return sortAsc ? -1 : 1;
      if (aValue > bValue) return sortAsc ? 1 : -1;
      return 0;
    });

  const totalPages = Math.ceil(filteredTutors.length / pageSize);
  const paginatedTutors = filteredTutors.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handleSetPassword = (student) => {
    setSelectedStudent(student);
    setNewPassword("");
    setConfirmPassword("");
    setShowPasswordModal(true);
  };

  const handleUpdatePassword = async () => {
    if (!newPassword || !confirmPassword) {
      toast.warn("Please fill in all password fields");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.warn("Passwords do not match!");
      return;
    }

    setBtnLoading(true);
    try {
      await updateUserPassword(selectedStudent.id, newPassword);
      toast.success("Password updated successfully!");
      setShowPasswordModal(false);
    } catch (error) {
      toast.error("Failed to update password");
    }
    setBtnLoading(false);
  };

  const handleToggleStatus = async (id, isActive) => {
    if (!id) return;
    if (user.id === id) {
      toast.error("You cannot change your own status");
      return;
    }
    if (window.confirm("Are you sure?") === false) return;

    setLoading(true);

    await updateUserStatus(id, isActive);
    setTutors((prev) =>
      prev.map((t) => (t.id === id ? { ...t, isActive } : t))
    );
    toast.success("Tutor status updated successfully!");
    setLoading(false);
  };

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Tutors</h2>
        <Button onClick={() => setShowModal(true)}>Add Tutor</Button>
      </div>

      {loading && <Loader />}

      <Table bordered hover responsive>
        <thead className="table-light">
          <tr>
            <th>#</th>
            <th>Name</th>
            <th>Email</th>
            <th>Phone</th>
            <th>Qualification</th>
            <th>Experience</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {paginatedTutors.length === 0 && (
            <tr>
              <td colSpan="8" className="text-center">
                No tutors found
              </td>
            </tr>
          )}
          {paginatedTutors.map((tutor, index) => (
            <tr
              key={tutor.id}
              className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <td>{index + 1}</td>
              <td>{tutor.name}</td>
              <td>{tutor.email}</td>
              <td>{tutor.phone}</td>
              <td>{tutor.qualification}</td>
              <td>{tutor.experience}</td>
              <td>
                <ToggleBtn
                  onToggle={(isChecked) =>
                    handleToggleStatus(tutor.id, isChecked)
                  }
                  isChecked={tutor.isActive}
                />
              </td>
              <td>
                <div className="flex space-x-2">
                  <Button
                    variant="info"
                    size="sm"
                    onClick={() => handleEdit(tutor)}
                  >
                    <PenIcon size={16} />
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDelete(tutor.id)}
                  >
                    <TrashIcon size={16} />
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleSetPassword(tutor)}
                  >
                    Set Password
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div className="flex justify-between items-center">
        <div>
          Page {currentPage} of {totalPages}
        </div>
        <div>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            Prev
          </Button>{" "}
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </div>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingId ? "Edit Tutor" : "Add Tutor"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Name *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter name"
                required
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email *</Form.Label>
              <Form.Control
                type="email"
                placeholder="Enter email"
                required
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Phone *</Form.Label>
              <Form.Control
                type="tel"
                placeholder="Enter phone"
                required
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Qualification *</Form.Label>
              <Form.Select
                required
                value={formData.qualification}
                onChange={(e) =>
                  setFormData({ ...formData, qualification: e.target.value })
                }
              >
                <option value="">Select qualification</option>
                <option value="Bachelor's Degree">Bachelor's Degree</option>
                <option value="Master's Degree">Master's Degree</option>
                <option value="PhD">PhD</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Experience *</Form.Label>
              <Form.Control
                type="number"
                placeholder="Enter experience"
                required
                value={formData.experience}
                onChange={(e) =>
                  setFormData({ ...formData, experience: e.target.value })
                }
              />
            </Form.Group>

            {/* Role selector - only visible for admins when editing */}
            {user?.role === 'admin' && editingId && (
              <Form.Group className="mb-3">
                <Form.Label>User Role</Form.Label>
                <Form.Select
                  value={formData.role || USER_ROLES.TUTOR}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      role: e.target.value,
                    })
                  }
                >
                  {Object.entries(USER_ROLES).map(([key, value]) => (
                    <option key={key} value={value}>
                      {value.replace("_", " ").toUpperCase()}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  ⚠️ Changing the role will move this user to a different category. Use with caution.
                </Form.Text>
              </Form.Group>
            )}
            <div className="flex justify-end">
              <Button variant="secondary" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={btnLoading}
                type="submit"
                className="ms-2"
              >
                {btnLoading ? "Saving..." : "Save"}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Password Modal */}
      <Modal
        show={showPasswordModal}
        onHide={() => setShowPasswordModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Set Password for {selectedStudent?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>New Password</Form.Label>
              <Form.Control
                type="password"
                value={newPassword}
                placeholder="Enter new password"
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Confirm Password</Form.Label>
              <Form.Control
                type="password"
                value={confirmPassword}
                placeholder="Confirm new password"
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowPasswordModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdatePassword}
            disabled={btnLoading}
          >
            {btnLoading ? "Saving..." : "Save Password"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Tutors;
